import {
  formatDeliveryTime as sharedFormatDeliveryTime,
  formatTimestamp as sharedFormatTimestamp,
} from '@/utils/dateFormatters'
import {
  getMappingListDisplay,
  getStringListDisplay,
} from '@api/hooks/useOrderDetails'
import type {
  DeliveryWindow,
  Drop,
  DropChanges,
  FieldChange,
  Load,
  LoadChanges,
  MappingKey,
  MatchedDrop,
} from './types'

/**
 * Get display value from a MappingKey
 */
export function getMappingDisplay(
  key: MappingKey | null | undefined,
): string | null {
  if (!key) return null
  return key.source_name || key.source_id || key.goid || null
}

/**
 * Compare two values and return a FieldChange if different (or always if forceInclude)
 */
function compareField(
  field: string,
  category: FieldChange['category'],
  before: string | number | null | undefined,
  after: string | number | null | undefined,
  forceInclude = false,
): FieldChange | null {
  const beforeStr = before != null ? String(before) : null
  const afterStr = after != null ? String(after) : null

  if (!forceInclude && beforeStr === afterStr) return null

  return { field, category, before: beforeStr, after: afterStr }
}

/**
 * Compare two Load objects and extract changes, grouped by load
 */
function compareLoads(
  beforeLoads: Load[] | undefined,
  afterLoads: Load[] | undefined,
  showAll = false,
): LoadChanges[] {
  const loadChanges: LoadChanges[] = []
  const maxLen = Math.max(beforeLoads?.length || 0, afterLoads?.length || 0)

  for (let i = 0; i < maxLen; i++) {
    const before = beforeLoads?.[i]
    const after = afterLoads?.[i]
    const fields: FieldChange[] = []

    const productChange = compareField(
      'Product',
      'loading',
      getMappingListDisplay(before?.products),
      getMappingListDisplay(after?.products),
      showAll,
    )
    if (productChange) fields.push(productChange)

    const terminalChange = compareField(
      'Terminal',
      'loading',
      getMappingListDisplay(before?.terminals),
      getMappingListDisplay(after?.terminals),
      showAll,
    )
    if (terminalChange) fields.push(terminalChange)

    const supplierChange = compareField(
      'Supplier',
      'loading',
      getMappingListDisplay(before?.suppliers),
      getMappingListDisplay(after?.suppliers),
      showAll,
    )
    if (supplierChange) fields.push(supplierChange)

    const priceTypeChange = compareField(
      'Price Type',
      'loading',
      getStringListDisplay(before?.price_types),
      getStringListDisplay(after?.price_types),
      showAll,
    )
    if (priceTypeChange) fields.push(priceTypeChange)

    const contractChange = compareField(
      'Contract',
      'loading',
      getStringListDisplay(before?.contracts),
      getStringListDisplay(after?.contracts),
      showAll,
    )
    if (contractChange) fields.push(contractChange)

    if (fields.length > 0) {
      loadChanges.push({ loadIndex: i, fields })
    }
  }

  return loadChanges
}

/**
 * Count total fields across all loads (for change detection)
 */
function countLoadFields(loads: LoadChanges[]): number {
  return loads.reduce((sum, load) => sum + load.fields.length, 0)
}

/**
 * Compute all changes between two Drop objects
 * When any supply change exists, show all supply fields for full context
 */
export function computeDropChanges(
  before: Drop | null | undefined,
  after: Drop | null | undefined,
): DropChanges {
  const destination: FieldChange[] = []

  // Destination changes (site)
  const siteChange = compareField(
    'Site',
    'destination',
    getMappingDisplay(before?.site),
    getMappingDisplay(after?.site),
  )
  if (siteChange) destination.push(siteChange)

  // Tank changes
  const tankChange = compareField(
    'Tank',
    'destination',
    before?.site?.tank_id,
    after?.site?.tank_id,
  )
  if (tankChange) destination.push(tankChange)

  // First pass: check if any supply changes exist
  const hasProductChange =
    getMappingDisplay(before?.product) !== getMappingDisplay(after?.product)
  const hasVolumeChange = before?.volume !== after?.volume
  const loadingChanges = compareLoads(before?.loads, after?.loads, false)
  const hasAnySupplyChange =
    hasProductChange || hasVolumeChange || countLoadFields(loadingChanges) > 0

  // Second pass: if any supply change, show all supply fields
  const showAllSupply = hasAnySupplyChange
  const productVolume: FieldChange[] = []

  // Product (always show if any supply change)
  const productChange = compareField(
    'Product',
    'product_volume',
    getMappingDisplay(before?.product),
    getMappingDisplay(after?.product),
    showAllSupply,
  )
  if (productChange) productVolume.push(productChange)

  // Volume (always show if any supply change)
  const volumeChange = compareField(
    'Volume',
    'product_volume',
    before?.volume ? `${before.volume.toLocaleString()} gal` : null,
    after?.volume ? `${after.volume.toLocaleString()} gal` : null,
    showAllSupply,
  )
  if (volumeChange) productVolume.push(volumeChange)

  // Loading changes (show all if any supply change)
  const loads = compareLoads(before?.loads, after?.loads, showAllSupply)

  return {
    destination,
    productVolume,
    loads,
    totalCount:
      destination.length + productVolume.length + countLoadFields(loads),
  }
}

/**
 * Match drops between before and after snapshots by array index
 * Returns paired arrays for comparison
 */
export function matchDrops(
  beforeDrops: Drop[] | null | undefined,
  afterDrops: Drop[] | null | undefined,
): MatchedDrop[] {
  const result: MatchedDrop[] = []
  const beforeList = beforeDrops || []
  const afterList = afterDrops || []
  const maxLen = Math.max(beforeList.length, afterList.length)

  for (let i = 0; i < maxLen; i++) {
    result.push({
      before: beforeList[i] || null,
      after: afterList[i] || null,
    })
  }

  return result
}

/**
 * Check if delivery windows are different
 */
export function hasDeliveryWindowChange(
  before: DeliveryWindow | null | undefined,
  after: DeliveryWindow | null | undefined,
): boolean {
  if (!before && !after) return false
  if (!before || !after) return true
  return before.start !== after.start || before.end !== after.end
}

/**
 * Format a timestamp as a readable date string
 * e.g., "Jan 13, 2026 at 9:04 AM"
 * @deprecated Import from '@/utils/dateFormatters' instead
 */
export const formatTimestamp = sharedFormatTimestamp

/**
 * Format a delivery window date/time
 * @deprecated Import from '@/utils/dateFormatters' instead
 */
export const formatDeliveryTime = sharedFormatDeliveryTime

/**
 * Get a human-readable label for a change type
 */
export function getChangeTypeLabel(changeType: string): string {
  const labels: Record<string, string> = {
    delivery_window_change: 'Delivery Window',
    supply_change: 'Supply',
    destination_change: 'Destination',
  }
  return labels[changeType] || changeType.replace(/_/g, ' ')
}
